import React, { useEffect, useState } from 'react';
import { Box, styled, alpha } from '@mui/system';
import { Typography } from '@mui/material';

import BalanceIcon from '@mui/icons-material/PaymentsOutlined';
import CreditIcon from '@mui/icons-material/CreditScoreOutlined';
import AvailableLimitIcon from '@mui/icons-material/MonetizationOnOutlined';
import WarningIcon from '@mui/icons-material/Warning';

import SbButton from '../../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '../../store';

import { getOutstandingBalanceForPrepaid, getWalletBalance } from '@Reducers/Wallet';

import AddMoneyModal from '../Transactions/Wallet/AddMoneyModal';

import { getCreditLimit, getOutstandingBalanceForPostpaid } from '@Reducers/Credit';

import { getOutstandingBalanceInvoiceForPostpaid } from '@Reducers/Credit/actions';

import { userLogin } from '@Services/hasAdminAccess';

import { getOutstandingBalanceInvoiceForPrepaid } from '@Reducers/Wallet/actions';

/* ----------------------------- STYLES (UPGRADED) ----------------------------- */

const NavigationBtnContainer = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '24px',
  alignItems: 'stretch',
}));

const NavigationBtn = styled('div')<{
  isOverLimit?: boolean;
  isHighlighted?: boolean;
}>(({ theme, isOverLimit, isHighlighted }) => ({
  padding: '20px',
  display: 'grid',
  gridTemplateColumns: '48px 1fr',
  alignItems: 'center',
  gap: '16px',

  height: '100%',
  boxSizing: 'border-box',

  borderRadius: '12px',
  position: 'relative',
  cursor: 'default',

  border: '1px solid',
  borderColor: isOverLimit ? theme.palette.error.main : theme.palette.divider,

  background: isOverLimit
    ? `linear-gradient(90deg, rgba(211,47,47,0.06), rgba(211,47,47,0.02))`
    : isHighlighted
    ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.04)})`
    : 'transparent',

  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',

  boxShadow: '0 2px 10px rgba(0,0,0,0.04)',

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
  },

  // Active left indicator like sidebar
  ...(isHighlighted && {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '15%',
      bottom: '15%',
      width: '4px',
      borderRadius: '0 8px 8px 0',
      background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.4)}`,
    },
  }),
}));

const IconWrapper = styled('div')<{ isOverLimit?: boolean }>(({ theme, isOverLimit }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  background: isOverLimit
    ? 'rgba(211,47,47,0.12)'
    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,

  color: '#fff',
  boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  marginTop: '4px',
  color: theme.palette.text.primary,
}));

const CurrencyBadge = styled('span')(({ theme }) => ({
  fontSize: '11px',
  padding: '2px 6px',
  borderRadius: '6px',
  background: theme.palette.action.hover,
  color: theme.palette.text.secondary,
}));

/* ----------------------------- COMPONENT ----------------------------- */

function Navigation() {
  const dispatch = useDispatch<AppDispatch>();
  const loginUser = useSelector((state: RootStateType) => state.loginUser);

  const [addMoneyModal, setAddMoneyModal] = useState(false);

  const { currentBalance, totalInvoiceAmtPaid, totalShipmentAmt, totalShipmentAmtPaid } = useSelector(
    (state: RootStateType) => state.Wallet,
  );

  const {
    creditLimit,
    totalInvoiceAmtPaid: totalInvoiceAmtPaidCredit,
    totalShipmentAmt: totalShipmentAmtCredit,
    totalShipmentAmtPaid: totalShipmentAmtPaidCredit,
  } = useSelector((state: RootStateType) => state.Credit);

  const orgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?._country?.currency?.symbol || '',
  );

  const isAccountHolder = loginUser?.profile?._accountId?.isAccountHolder;
  const isPrepaid = loginUser?.profile?._accountId?.isPrepaid;
  const isUserLogin = userLogin();

  const outStandingBalanceResult = totalShipmentAmt - totalShipmentAmtPaid - totalInvoiceAmtPaid;

  const outStandingBalanceResultCredit =
    totalShipmentAmtCredit - totalShipmentAmtPaidCredit - totalInvoiceAmtPaidCredit;

  useEffect(() => {
    if (typeof isAccountHolder !== 'boolean') return;

    if (isAccountHolder) {
      dispatch(getWalletBalance());
      dispatch(getCreditLimit());
      dispatch(getOutstandingBalanceForPostpaid());
      dispatch(getOutstandingBalanceInvoiceForPostpaid());
    } else {
      dispatch(getCreditLimit());
      dispatch(getOutstandingBalanceForPrepaid());
      dispatch(getOutstandingBalanceInvoiceForPrepaid());
    }
  }, [isAccountHolder]);

  const availableLimit = creditLimit - outStandingBalanceResultCredit;

  const outstandingBalance = isAccountHolder ? outStandingBalanceResultCredit : outStandingBalanceResult;

  return (
    <NavigationBtnContainer>
      {addMoneyModal && <AddMoneyModal onClose={() => setAddMoneyModal(false)} />}

      {/* OUTSTANDING */}
      <NavigationBtn>
        <IconWrapper>
          <BalanceIcon />
        </IconWrapper>

        <div>
          <StatLabel>
            Outstanding Balance <CurrencyBadge>{orgCountryCurrency}</CurrencyBadge>
          </StatLabel>
          <StatValue>{outstandingBalance?.toFixed(2)}</StatValue>
        </div>
      </NavigationBtn>

      {/* CREDIT LIMIT */}
      {isAccountHolder && !isPrepaid && (
        <>
          <NavigationBtn isHighlighted>
            <IconWrapper>
              <CreditIcon />
            </IconWrapper>

            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <StatLabel>
                Credit Limit <CurrencyBadge>{orgCountryCurrency}</CurrencyBadge>
              </StatLabel>
              <StatValue>{creditLimit}</StatValue>

              <SbButton
                size="small"
                variant="contained"
                sx={{
                  mt: 1,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  width: 'fit-content',
                }}
              >
                Update Limit
              </SbButton>
            </div>
          </NavigationBtn>

          {/* AVAILABLE LIMIT */}
          <Box sx={{ position: 'relative' }}>
            <NavigationBtn isOverLimit={availableLimit < 0}>
              <IconWrapper isOverLimit={availableLimit < 0}>
                <AvailableLimitIcon />
              </IconWrapper>

              <div>
                <StatLabel>
                  Available Limit <CurrencyBadge>{orgCountryCurrency}</CurrencyBadge>
                </StatLabel>
                <StatValue>{availableLimit?.toFixed(2)}</StatValue>
              </div>
            </NavigationBtn>

            {availableLimit < 0 && (
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'error.light',
                }}
              >
                <WarningIcon sx={{ fontSize: 18, color: 'error.main' }} />
                <Typography variant="body2" color="error.dark">
                  {isUserLogin
                    ? 'Your account has exceeded its credit limit'
                    : 'This account has exceeded its credit limit'}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* WALLET */}
      {isPrepaid && (
        <NavigationBtn>
          <IconWrapper>
            <CreditIcon />
          </IconWrapper>

          <div>
            <StatLabel>
              Wallet Balance <CurrencyBadge>{orgCountryCurrency}</CurrencyBadge>
            </StatLabel>
            <StatValue>{currentBalance || 0}</StatValue>

            <SbButton variant="outlined" sx={{ mt: 1, borderRadius: '8px' }} onClick={() => setAddMoneyModal(true)}>
              Add Money
            </SbButton>
          </div>
        </NavigationBtn>
      )}
    </NavigationBtnContainer>
  );
}

export default Navigation;
